// Tela da Diretoria na Visão Geral (#6495): Igor e Vinicius Alves, mesma tela.
//
// Usa os MESMOS ganchos e o MESMO catálogo de KPIs da aba CEO do Painel Executivo,
// com o "agora" no fim do mês escolhido: o número aqui é o número da aba.
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, PieChart, Sun, TrendingUp } from 'lucide-react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGeracaoCeo, useKpisDepartamento, useMetasFaturamento, useParadasCeo, useUsinasCeo } from '../../Dashboard/PainelExecutivo/sections/ceo/useCeoDados';
import { useContextoKpis } from '../../Dashboard/PainelExecutivo/sections/ceo/useContextoKpis';
import { useFinanceiroDashboardData } from '../../Dashboard/PainelExecutivo/sections/financeiro/useFinanceiroDashboardData';
import { useComercialDashboardData } from '../../Dashboard/PainelExecutivo/sections/comercial/useComercialDashboardData';
import { useMetasMensais } from '../../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';
import { COR_FAIXA } from '../../Dashboard/PainelExecutivo/sections/ceo/ui';
import { FILTROS_DASHBOARD_INICIAIS } from '../../Dashboard/PainelExecutivo/types';
import { computeEventFunnel } from '../../CRM/Analytics/eventFunnel';
import { filtrarPorDepartamento } from '../../../src/lib/dashboard/filtrarNegociacoesPorDepartamento';
import { agruparParadas, calcularDinheiroDoPeriodo, ultimoMesFechado } from '../../../src/lib/dashboard/painelCeo';
import { kpiAutomatico } from '../../../src/lib/dashboard/kpisAutomaticos';
import { rotuloDoMes } from '../../../src/lib/dashboard/metasMensais';
import { separarPorCanal } from '../../../src/lib/visaoGeral/comercial';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { avaliarKpi, inadimplenciaRegraBonus, saudeDosDepartamentos, type KpiAvaliado } from '../../../src/lib/visaoGeral/diretoria';
import { lerMes } from '../../../src/lib/visaoGeral/periodo';
import { BlocosDoDia } from '../BlocosDoDia';
import { composicaoDaEnergia, useEnergiaPorCanal } from '../useEnergiaPorCanal';
import { CartaoCustoPorCliente, CartaoOrigemDosContratos, CartaoQualidadeDoLead } from '../blocosComercialFase2';
import { useOrigemDosContratos } from '../useComercialFase2';
import { CabecalhoVisaoGeral, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral } from '../componentes';
import type { PropsTelaVisaoGeral } from '../tipos';

const reais = (v: number | null | undefined) => formatarValorKpi(v ?? null, 'reais');
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** Chip de comparação de um KPI, no sentido dele (verde = melhorou). */
function comparacaoDe(k: KpiAvaliado | null, parcial: boolean) {
  if (!k || k.melhora == null || !Number.isFinite(k.melhora) || Math.abs(k.melhora) < 0.005 || !k.rotuloAnterior) return undefined;
  const melhorou = k.melhora > 0;
  return {
    texto: `${pct(Math.abs(k.melhora))} ${melhorou ? 'melhor' : 'pior'} que ${k.rotuloAnterior}${parcial ? ' no mesmo ponto' : ''}`,
    bom: melhorou,
    sobe: k.sentido === 'maior' ? melhorou : !melhorou,
  };
}

/** Nome da meta no cartão, conforme de onde ela veio. */
const ROTULO_META: Record<KpiAvaliado['origemMeta'], (k: KpiAvaliado) => string> = {
  mes: (k) => `meta de ${rotuloDoMes(k.mesMeta)}`,
  validada: () => 'meta',
  proposta: () => 'meta proposta',
  sugerida: () => 'meta sugerida',
};

/** Meta do KPI no cartão: valor, de onde veio e quanto já devia ter andado no mês em curso. */
function metaDe(k: KpiAvaliado | null, rotulo?: string) {
  if (!k || k.carregando || k.meta <= 0) return undefined;
  const parcial = k.metaAteAgora < k.meta - 1e-9;
  const base = `${k.origemMeta === 'mes' ? ROTULO_META.mes(k) : rotulo ?? ROTULO_META[k.origemMeta](k)} ${k.sentido === 'menor' ? 'até ' : ''}${formatarValorKpi(k.meta, k.unidade)}`;
  return { texto: parcial ? `${base} · ${formatarValorKpi(k.metaAteAgora, k.unidade)} até hoje` : base, atingido: k.atingimento };
}

// Destino do clique em cada departamento: o resultado por pessoa está na aba Equipes;
// geradoras não têm pessoas, então vão para a aba delas (decisão de 27/10/2026).
const DESTINO_DEPARTAMENTO: Record<string, string> = {
  geradoras: '/dashboard/painel-executivo/geradoras',
};
const destinoDepartamento = (id: string) => DESTINO_DEPARTAMENTO[id] ?? '/dashboard/painel-executivo/equipes';

const TelaDiretoria: React.FC<PropsTelaVisaoGeral> = ({ pessoa, definicao, periodo, aoMudarMes }) => {
  // O "agora" das contas: hoje no mês corrente; o último instante do mês nos meses fechados.
  const { agora, inicio, fim } = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    return {
      agora: periodo.corrente ? new Date() : new Date(ano, mes0 + 1, 0, 23, 59, 59),
      inicio: new Date(ano, mes0, 1),
      fim: new Date(ano, mes0 + 1, 1),
    };
  }, [periodo.mes, periodo.corrente]);

  const { usinas, fator } = useUsinasCeo();
  const geracao = useGeracaoCeo(usinas, inicio, fim);
  const paradas = useParadasCeo(usinas, inicio, agora);
  const metas = useMetasFaturamento();
  const fin = useFinanceiroDashboardData();
  const com = useComercialDashboardData();
  const { kpis: kpisSalvos } = useKpisDepartamento();
  const { metas: metasMensais } = useMetasMensais();
  const eventos = useMemo(() => agruparParadas(paradas.episodios, usinas), [paradas.episodios, usinas]);
  const fechado = useMemo(() => ultimoMesFechado(fin.dados, agora), [fin.dados, agora]);
  const ctx = useContextoKpis({
    agora, usinas, fator, linhas: geracao.linhas, eventos, paradasCarregando: paradas.loading, mesFechado: fechado, metas,
  });

  // KPI validado pelo Igor entra com a meta dele; a meta do mês (aba Metas) vence as duas.
  const kpi = (id: string) => avaliarKpi(
    kpiAutomatico(id), ctx, kpisSalvos.find(k => k.calculo_automatico === id && k.status === 'validado'), metasMensais,
  );
  const faturado = kpi('ges_faturado');
  const clientes = kpi('ges_clientes');
  const energia = kpi('com_kwh');
  const geracaoMeta = kpi('ger_meta');
  const horasParadas = kpi('ger_horas');

  const temFaturas = fin.dados.length > 0;
  const dinheiro = useMemo(() => (temFaturas ? {
    atual: calcularDinheiroDoPeriodo(fin.dados, periodo.inicio, periodo.fim, new Date(), null),
    anterior: calcularDinheiroDoPeriodo(fin.dados, periodo.anterior.inicio, periodo.anterior.fim, new Date(), null),
  } : null), [fin.dados, temFaturas, periodo.inicio, periodo.fim, periodo.anterior.inicio, periodo.anterior.fim]);
  // Inadimplência é uma foto de hoje, qualquer que seja o mês escolhido: a regra olha boletos com 30+ dias de cobrança.
  const inadimplencia = useMemo(() => (temFaturas ? inadimplenciaRegraBonus(fin.dados, new Date()) : null), [fin.dados, temFaturas]);

  const saude = useMemo(() => saudeDosDepartamentos(ctx, kpisSalvos, metasMensais), [ctx, kpisSalvos, metasMensais]);
  const energiaCanais = useEnergiaPorCanal(com.dados, com.profilesById, periodo);
  const origens = useOrigemDosContratos(com.dados, periodo);
  const deOndeVeio = energiaCanais ? composicaoDaEnergia(energiaCanais, periodo.anterior.rotulo) : null;
  // Meta de energia só aparece quando alguém a definiu: a sugerida sai do melhor mês recente, e SET/2026
  // tem a licitação de Morrinhos (sugeriria 190 mil kWh).
  const metaEnergia = energia && (energia.origemMeta === 'mes' || energia.origemMeta === 'validada') ? metaDe(energia) : undefined;

  // Tendência de 12 meses até o mês escolhido: R$ dos boletos que venceram no mês, quanto deles foi pago, e kWh aprovado.
  const tendencia = useMemo(() => {
    if (!temFaturas) return null;
    const { ano, mes0 } = lerMes(periodo.mes)!;
    const filtradas = com.dados.length ? filtrarPorDepartamento(com.dados, com.profilesById, FILTROS_DASHBOARD_INICIAIS.departamentos) : null;
    const licitacoes = filtradas ? separarPorCanal(filtradas, com.profilesById).get('licitacao') ?? [] : [];
    const hoje = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(ano, mes0 - 11 + i, 1);
      const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const ini = `${mes}-01`;
      const ultimo = `${mes}-${String(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      const din = calcularDinheiroDoPeriodo(fin.dados, ini, ultimo, hoje, null);
      const aprovado = filtradas
        ? computeEventFunnel(filtradas, 'personalizado', { inicio: ini, fim: ultimo }).find(e => e.key === 'termo_aprovado')
        : null;
      const licitacao = licitacoes.length
        ? computeEventFunnel(licitacoes, 'personalizado', { inicio: ini, fim: ultimo }).find(e => e.key === 'termo_aprovado')
        : null;
      return {
        rotulo: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
        emitido: Math.round(din.esperado),
        recebido: Math.round(din.recebido),
        kwh: aprovado ? Math.round(aprovado.kwh) : null,
        kwhLicitacao: licitacao ? Math.round(licitacao.kwh) : 0,
      };
    });
  }, [fin.dados, temFaturas, com.dados, com.profilesById, periodo.mes]);

  const parcial = periodo.corrente;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} />

      <BlocosDoDia pessoa={pessoa} perfil="diretoria" />

      <SecaoVisaoGeral titulo="O mês" subtitulo={periodo.rotulo} colunas={3}>
        <CartaoIndicador
          titulo={`Faturamento de ${faturado?.periodo || 'último mês fechado'}`}
          valor={faturado && !faturado.carregando ? formatarValorKpi(faturado.atual, 'reais') : null}
          meta={metaDe(faturado, ctx.metaFaturamentoUsinas > 0 ? 'meta das usinas' : undefined)}
          comparacao={comparacaoDe(faturado, false)}
          detalhe="Parte da Vigor nas cobranças do último mês de contas fechado."
          para="/dashboard/painel-executivo/ceo"
          origem="Mesma conta do Faturamento por usina, na aba CEO do Painel Executivo."
        />
        <CartaoIndicador
          titulo={parcial ? 'Entrou no mês' : `Entrou em ${periodo.rotulo.toLowerCase()}`}
          valor={dinheiro ? reais(dinheiro.atual.caixa) : null}
          detalhe={dinheiro ? (parcial
            ? `Ainda vence no mês: ${reais(dinheiro.atual.aVencer)}`
            : `Dos boletos do mês, ${reais(dinheiro.atual.vencido)} seguem em aberto`) : undefined}
          comparacao={dinheiro && dinheiro.anterior.caixa > 0 ? (() => {
            const v = dinheiro.atual.caixa / dinheiro.anterior.caixa - 1;
            return { texto: `${pct(Math.abs(v))} ${v >= 0 ? 'acima' : 'abaixo'} de ${periodo.anterior.rotulo}`, bom: v >= 0, sobe: v >= 0 };
          })() : undefined}
          para="/financeiro/previsao-recebiveis"
          origem="Pagamentos com data no período (mesma conta do Dinheiro do período, na aba CEO)."
        />
        <CartaoIndicador
          titulo="Inadimplência"
          valor={inadimplencia ? (inadimplencia.taxa == null ? '—' : pct(inadimplencia.taxa)) : null}
          detalhe={inadimplencia ? `Boletos vencidos de ${dataCurta(inadimplencia.janela.inicio)} a ${dataCurta(inadimplencia.janela.fim)} ainda em aberto · estoque vencido ${reais(inadimplencia.estoque)}` : undefined}
          para="/dashboard/painel-executivo/financeiro"
          origem="Regra do plano de bônus: vencido há mais de 30 dias e em aberto ÷ valor desses boletos, nos 3 meses anteriores."
        />
        <CartaoIndicador
          titulo="Energia contratada"
          valor={energia && !energia.carregando ? formatarValorKpi(energia.atual, 'kwh') : null}
          meta={metaEnergia}
          detalhe={energia && !energia.carregando && !metaEnergia
            ? `kWh em contratos aprovados no mês, licitações incluídas. Sem meta para ${rotuloDoMes(energia.mesMeta)}: defina na aba Metas do Painel Executivo.`
            : 'kWh em contratos aprovados no mês, licitações incluídas.'}
          composicao={deOndeVeio?.composicao}
          comparacao={comparacaoDe(energia, parcial)}
          nota={deOndeVeio?.nota}
          para="/dashboard/painel-executivo/comercial"
          origem="Funil do CRM, a mesma conta da aba Comercial do Painel Executivo. Canal pela mesma regra do filtro de canal de lá."
        />
        <CartaoIndicador
          titulo={`Clientes faturados em ${clientes?.periodo || 'último mês fechado'}`}
          valor={clientes && !clientes.carregando ? formatarValorKpi(clientes.atual, 'numero') : null}
          detalhe="UCs diferentes com cobrança no mês de contas."
          comparacao={comparacaoDe(clientes, false)}
          para="/financeiro/ucs"
        />
        <CartaoIndicador
          titulo="Usinas: geração x meta"
          valor={geracaoMeta && !geracaoMeta.carregando ? formatarValorKpi(geracaoMeta.atual, 'percentual') : null}
          meta={metaDe(geracaoMeta, 'meta')}
          detalhe={horasParadas && !horasParadas.carregando ? `${formatarValorKpi(horasParadas.atual, 'horas')} de usina parada no mês` : undefined}
          comparacao={comparacaoDe(geracaoMeta, false)}
          para="/geradoras"
          origem="Energia gerada ÷ meta do laudo, até hoje (aba CEO)."
        />
        <CartaoOrigemDosContratos linhas={origens} />
        <CartaoCustoPorCliente mes={periodo.mes} />
        <CartaoQualidadeDoLead mes={periodo.mes} corrente={periodo.corrente} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo="Saúde dos departamentos" acao={{ rotulo: 'KPIs na aba CEO', para: '/dashboard/painel-executivo/ceo' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {saude.map(dep => (
              <Link key={dep.id} to={destinoDepartamento(dep.id)} className="block rounded-xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-900 dark:text-white">{dep.rotulo}</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${dep.carregando ? 'text-gray-400' : COR_FAIXA[dep.faixa].texto}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${dep.carregando ? 'bg-gray-200 dark:bg-gray-700 animate-pulse' : COR_FAIXA[dep.faixa].barra}`} />
                    {dep.carregando ? 'carregando' : COR_FAIXA[dep.faixa].rotulo}
                  </span>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {dep.kpis.map(k => (
                    <li key={k.chave} className="flex items-baseline justify-between gap-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400 min-w-0 truncate" title={k.nome}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle ${k.carregando ? 'bg-gray-200' : COR_FAIXA[k.faixa].barra}`} />
                        {k.nome}
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap tabular-nums">
                        {k.carregando ? '…' : formatarValorKpi(k.atual, k.unidade)}
                        {!k.carregando && k.meta > 0 && <span className="font-normal text-gray-400"> / {formatarValorKpi(k.meta, k.unidade)}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Meta de cada indicador: a do mês na aba Metas; sem ela, a validada na aba CEO; sem ela, a sugerida pelo sistema.
            {' '}<Link to="/dashboard/painel-executivo/metas" className="font-semibold text-primary hover:underline">Definir metas</Link>.
            {' '}Clique num departamento para ver o resultado por pessoa.
          </p>
        </PainelDetalhe>

        <PainelDetalhe titulo="Últimos 12 meses">
          {tendencia === null ? (
            <div className="h-64 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : (
            <div className="h-72 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={tendencia} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
                  <XAxis dataKey="rotulo" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="r" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} width={56}
                    tickFormatter={(v: number) => (v >= 1e6 ? `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi` : v >= 1000 ? `${Math.round(v / 1000)} mil` : String(v))} />
                  <YAxis yAxisId="k" orientation="right" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} width={56}
                    tickFormatter={(v: number) => `${Math.round(v / 1000)} mil`} />
                  <Tooltip
                    formatter={(valor: number, nome: string) => (nome === 'kWh aprovado' || nome === 'dos quais licitação' ? `${valor.toLocaleString('pt-BR')} kWh` : reais(valor))}
                    contentStyle={{ borderRadius: 12, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="r" dataKey="emitido" name="Boletos do mês (R$)" fill="#C9D3A8" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="r" dataKey="recebido" name="Pago desses boletos (R$)" fill="#5F6C37" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="k" dataKey="kwh" name="kWh aprovado" stroke="#D18A1F" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  {tendencia.some(t => t.kwhLicitacao > 0) && (
                    <Line yAxisId="k" dataKey="kwhLicitacao" name="dos quais licitação" stroke="#D18A1F" strokeDasharray="4 3" strokeWidth={1.5} dot={false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">Boletos pelo mês de vencimento. O kWh aprovado é o mesmo número do card "Energia contratada", licitações incluídas; a linha tracejada mostra quanto veio delas.</p>
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Painel Executivo', para: '/dashboard/painel-executivo', icone: PieChart },
        { rotulo: 'Previsão de Recebíveis', para: '/financeiro/previsao-recebiveis', icone: TrendingUp },
        { rotulo: 'Geradoras', para: '/geradoras', icone: Sun },
        { rotulo: 'Projetos', para: '/projetos', icone: Briefcase },
      ]} />
    </div>
  );
};

export default TelaDiretoria;
